import { commonFilters, IFiltersPagination } from "@app/common/types/http";
import { useAuthentication } from "@app/store/useAuthtentication";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  bankListService,
  companyClientService,
  companyListService,
  companyTypeService,
  contractListService,
  getCancellationReasonsService,
  createPatientService,
  depositTypeService,
  doctorListService,
  documentTypeService,
  extensionListService,
  franchiseService,
  terminalService,
  getActiveUsersService,
  getUsersByModuleService,
  getAffiliationTypes,
  getEducationLevels,
  getCashClosingConfig,
  getCitiesService,
  getContractPopulations,
  getContracts,
  getCopaymentRates,
  getEthnicGroups,
  getProvincesService,
  getStratumsService,
  listPatientService,
  listExternalClientsService,
  listSitesService,
  listSitesToStartService,
  patientInformationService,
  statusListService,
  updatePatientInformationService
} from "./common";
import {
  ICashClosingConfigFilters,
  IContractParams,
  IContractPopulationParams,
  ICopaymentRateParams,
  ICreatePatientPayload
} from "./types";
import { IPatientPayload } from "@app/features/drugDelivery/order/patient/types";

export function useGetCompanyTypeList(filters: commonFilters, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["companyTypeList"],
    queryFn: async () => await companyTypeService(filters, token)
  });
  return query;
}
export function useGetPatientList(
  filters: { orderByType?: string } & IFiltersPagination,
  enabled = true
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    queryKey: ["patients", filters.page, filters?.search, filters.orderByType],
    queryFn: async () => await listPatientService(filters, token)
  });

  return query;
}
export function useGetExternalClientList(
  filters: { orderByType?: string } & IFiltersPagination,
  enabled = true
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    queryKey: [
      "externalClients",
      filters.page,
      filters?.search,
      filters.orderByType,
      filters.documentNumber,
      filters.documentType
    ],
    queryFn: async () => await listExternalClientsService(filters, token)
  });

  return query;
}
export function useGetPatientInformation(
  filters: { id?: string | number } & IFiltersPagination,
  enabled = true
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["patients", filters.id],
    queryFn: async () => await patientInformationService(filters, token)
  });

  return query;
}
export function useGetCompanyClientList(
  filters: { contractCompanyType?: string } & commonFilters,
  enabled = true
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["companyClients"],
    queryFn: async () => await companyClientService(filters, token)
  });

  return query;
}
export function useGetSiteList(filters: { modality?: string } & commonFilters, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["sites"],
    queryFn: async () => await listSitesService(filters, token)
  });

  return query;
}
export function useGetDoctorList(filters: commonFilters, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["doctors"],
    queryFn: async () => await doctorListService(filters, token)
  });

  return query;
}
export function useGetStatusList(filters: { staFunctionality: string }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["status"],
    queryFn: async () => await statusListService(filters, token)
  });

  return query;
}

export function useGetDocumentTypes() {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const query = useQuery({
    gcTime: 0,
    queryKey: ["documentTypes"],
    queryFn: async () => await documentTypeService(token)
  });

  return query;
}
export function useGetCompanyList() {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const eaccount = useAuthentication((store) => store.auth.dataBiowel.idAccount);
  const query = useQuery({
    gcTime: 0,
    queryKey: ["companyList"],
    queryFn: async () => await companyListService(token, eaccount)
  });

  return query;
}
export function useGetContractList(
  filters: {
    slim: number;
    corporateClient: number;
  },
  enabled: boolean
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const query = useQuery({
    gcTime: 0,
    enabled,
    queryKey: ["companyList", filters.corporateClient],
    queryFn: async () => await contractListService(filters, token)
  });

  return query;
}

export function useGetExtensionList() {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const query = useQuery({
    gcTime: 0,
    queryKey: ["extensions"],
    queryFn: async () => await extensionListService(token)
  });

  return query;
}

export function useGetSiteListToStart(
  filters: { userId?: number } & commonFilters,
  enabled = true
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["sitesToStart", filters.eaccount],
    queryFn: async () => await listSitesToStartService(filters, token)
  });

  return query;
}

export function useGetBankEntities(filters: { eaccount: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    queryKey: ["bankList", filters.eaccount],
    queryFn: async () => await bankListService(filters, token)
  });

  return query;
}
export function useGetDepositType(filters: { eaccount: number; active?: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    queryKey: ["depositList", filters.eaccount],
    queryFn: async () => await depositTypeService({ ...filters, active: 1 }, token)
  });

  return query;
}
export function useGetFranchise(filters: { eaccount: number }, enabled = true) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    queryKey: ["franchiseList", filters.eaccount],
    queryFn: async () => await franchiseService(filters, token)
  });

  return query;
}

export function useGetTerminals(
  filters: { eaccount: number; perpage?: number; page?: number },
  enabled = true
) {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    queryKey: ["terminalList", filters.eaccount],
    queryFn: async () => await terminalService(filters, token)
  });

  return query;
}

export const useGetCashClosingConfig = (filters: ICashClosingConfigFilters, enabled = true) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["cashClosingConfig", filters.userId, filters.eaccount, filters.siteId],
    queryFn: () => getCashClosingConfig(filters, token)
  });

  return query;
};

export const useGetProvinces = (accountId: number) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["provinces", accountId],
    queryFn: () => getProvincesService(accountId, token),
    enabled: !!accountId
  });

  return { data: query.data?.data || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetCities = (provinceId?: number) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["cities", provinceId],
    queryFn: () => getCitiesService(token, provinceId),
    enabled: !!provinceId
  });

  return { data: query.data?.data || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetStratums = () => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["stratums"],
    queryFn: async () => await getStratumsService(token)
  });

  return { data: query.data?.results || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetAffiliationTypes = (accountId: number) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["affiliationTypes", accountId],
    queryFn: () => getAffiliationTypes(accountId, token),
    enabled: !!accountId
  });

  return { data: query.data?.results || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetEthnicGroups = (accountId: number) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["ethnicGroups", accountId],
    queryFn: () => getEthnicGroups(accountId, token),
    enabled: !!accountId
  });

  return { data: query.data?.results || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetEducationLevels = (accountId: number) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["educationLevels", accountId],
    queryFn: () => getEducationLevels(accountId, token),
    enabled: !!accountId
  });

  return { data: query.data?.results || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetClientContracts = (params: Partial<IContractParams>) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["clientContracts", params],
    queryFn: () => getContracts(params, token),
    enabled: !!params.client
  });

  return { data: query.data?.results || [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetContractPopulations = (params: Partial<IContractPopulationParams>) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["contractPopulations", params],
    queryFn: () => getContractPopulations(params as IContractPopulationParams, token),
    enabled: !!params.contract
  });

  return { data: query.data?.results ?? [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useGetCopaymentRates = (params: Partial<ICopaymentRateParams>) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    gcTime: 0,
    queryKey: ["copaymentRates", params],
    queryFn: () => getCopaymentRates(params, token),
    enabled: !!params.corpclientId
  });

  return { data: query.data?.results ?? [], isLoading: query.isLoading, refetch: query.refetch };
};

export const useUpdatePatientInformation = (onSuccess: () => void) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const mutation = useMutation({
    mutationKey: ["updatePatientInformation"],
    mutationFn: async (payload: IPatientPayload) =>
      await updatePatientInformationService(payload, token),
    onSuccess: () => {
      onSuccess();
    }
  });

  return mutation;
};

export const useGetActiveUsers = (enabled = true) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["activeUsers"],
    queryFn: async () => await getActiveUsersService({ page: 1, active: 1 }, token)
  });

  return query;
};

export const useGetUsersByModule = (enabled = true) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);
  const idModule = useAuthentication((state) => state.auth.dataBiowel.idModule);
  const eaccount = useAuthentication((state) => state.auth.dataBiowel.idAccount);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["usersByModule", idModule],
    queryFn: async () => await getUsersByModuleService({ id_module: idModule, eaccount }, token)
  });

  return query;
};

export const useCreatePatient = (onSuccess: () => void, onError?: (e: Error) => void) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const mutation = useMutation({
    mutationKey: ["createPatient"],
    mutationFn: async (payload: ICreatePatientPayload) =>
      await createPatientService(payload, token),
    onSuccess: () => onSuccess(),
    onError: (e: Error) => onError?.(e)
  });

  return mutation;
};

export const useGetCancellationReasons = (attributableTo: string, enabled = true) => {
  const token = useAuthentication((state) => state.auth.dataBiowel.token);

  const query = useQuery({
    enabled,
    gcTime: 0,
    queryKey: ["cancellationReasons", attributableTo],
    queryFn: async () => await getCancellationReasonsService({ attributableTo }, token)
  });

  return { data: query.data?.results ?? [], isLoading: query.isLoading };
};
